import { FC, ReactElement, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import cn from 'classnames';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { TabTypes } from 'src/page-components/drug/utils';
import useGTAG, { GTAGPrefix } from 'src/hooks/useGTAG';

const PrevButton = dynamic(
  () => import('./Buttons').then((module) => module.PrevButton),
  {
    ssr: false
  }
);

const NextButton = dynamic(
  () => import('./Buttons').then((module) => module.NextButton),
  {
    ssr: false
  }
);

export type Tab = {
  id: TabTypes | string;
  title: ReactElement;
  options?: { href?: string; scroll?: boolean; shallow?: boolean };
  gtag?: string;
};

interface TabProps {
  selected: boolean;
  tab: Tab;
  gtagPrefix?: GTAGPrefix;
  onClick: () => void;
  options?: { href?: string; scroll?: boolean; shallow?: boolean };
}

interface Props {
  tabList: Tab[];
  forceSelectedTab?: Tab;
  gtagPrefix?: GTAGPrefix;
  onSwitch?: (tab: Tab) => void;
}

const Tab: FC<TabProps> = ({
  selected,
  tab,
  gtagPrefix,
  onClick,
  options: { href, scroll = true, shallow = false }
}) => {
  const { trackGtagEvent } = useGTAG();

  const className = cn(
    'relative embla__slide inline-block items-center justify-center mr-[11px] h-[38px] box-border z-200',
    selected
      ? 'text-black opacity-90 fs-body-bold border-b-2 border-bahamaBlue'
      : `border-none opacity-50 text-black fs-body-bold`
  );

  const title = <p className="z-10 px-2 whitespace-nowrap">{tab.title}</p>;

  return href ? (
    <Link
      className={cn(className, 'flex')}
      href={href}
      scroll={scroll}
      shallow={shallow}
      prefetch={false}
      onClick={() => {
        onClick();
        setTimeout(() => {
          trackGtagEvent(`${gtagPrefix}_${tab.gtag}`);
        }, 250);
      }}
    >
      {title}
    </Link>
  ) : (
    <button className={className} onClick={onClick}>
      {title}
    </button>
  );
};

const Tabs: FC<Props> = ({
  tabList = [],
  forceSelectedTab,
  gtagPrefix,
  onSwitch
}) => {
  const [selectedTab, setSelectedTab] = useState<Tab>(tabList[0]);

  const [viewportRef, embla] = useEmblaCarousel({
    startIndex: 0,
    loop: false,
    align: 0,
    inViewThreshold: 1
  });

  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false);

  const onSelect = () => {
    if (!embla) return;
    setPrevBtnEnabled(
      embla.canScrollPrev() && !embla?.slidesInView()?.includes(0)
    );
    setNextBtnEnabled(
      embla.canScrollNext() &&
        !embla?.slidesInView()?.includes(tabList?.length - 1)
    );
  };

  const scrollPrev = () => embla?.scrollPrev();

  const scrollNext = () => embla?.scrollNext();

  useEffect(() => {
    if (!embla) return;
    embla.on('select', onSelect);
    embla.on('scroll', onSelect);
    onSelect();
  }, [embla, onSelect]);

  const handleSelectTab = (tab: Tab, index: number) => {
    onSelect();
    setSelectedTab(tab);

    if (!embla?.slidesInView()?.includes(index)) {
      embla.scrollNext();
    }

    if (onSwitch) {
      onSwitch(tab);
    }
  };

  useEffect(() => {
    if (forceSelectedTab) {
      setSelectedTab(forceSelectedTab);
    }
  }, [forceSelectedTab]);

  return (
    <div className="flex relative overflow-hidden items-center justify-center">
      {prevBtnEnabled && (
        <PrevButton onClick={scrollPrev} enabled={prevBtnEnabled} />
      )}
      <div
        className="relative w-full embla__viewport overflow-hidden"
        ref={viewportRef}
      >
        <div className="flex w-full embla__container border-b border-silver">
          {tabList.map((tabItem, index) => {
            return (
              <Tab
                key={tabItem.id}
                tab={tabItem}
                selected={tabItem.id === selectedTab.id}
                onClick={() => handleSelectTab(tabItem, index)}
                options={tabItem?.options || {}}
                gtagPrefix={gtagPrefix}
              />
            );
          })}
        </div>
      </div>
      {nextBtnEnabled && (
        <NextButton onClick={scrollNext} enabled={nextBtnEnabled} />
      )}
      <div className="absolute h-[1px] w-full bg-silver border-b border-silver bottom-0 left-0" />
    </div>
  );
};

export default Tabs;
